import { Dispatch, Fragment, useEffect, useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { onUpdateBooking } from "../../slices/thunk";
import Modal from "../../Common/Components/Modal";
import { Link } from "react-router-dom";
import { currencyFormatter } from "../../helpers/formatter";
import { Booking, serviceBookingStatus } from "../../services/booking.service";
import { PaymentStatusHelper } from "../../Common/Components/Status";
import { doCreateInvoice, doGetInvoiceBySourceId, doUpdateInvoice, Invoice } from "../../services/invoice.service";
import { Project } from "../../services/project.service";
import ReactQuill from "react-quill";
import { customerPortalUrl } from "../../helpers/constants/config.constants";

type BookingDetailModalProps = {
  isOpen: boolean
  setIsOpen: Dispatch<boolean>
  booking: Booking | undefined
  setBooking: Dispatch<Booking>
  project: Project | undefined
  projectSetting: Project | undefined
}

const BookingDetailModal = (
  { isOpen, setIsOpen, booking, setBooking, project, projectSetting }: BookingDetailModalProps
) => {

  const [invoice, setInvoice] = useState<Invoice | undefined>()
  const [invoiceNotes, setInvoiceNotes] = useState<string>('')

  const dispatch = useDispatch<any>();

  useEffect(() => {
    if (booking?.serviceBookingId) {
      doGetInvoiceBySourceId(booking?.serviceBookingId).then((res: any) => {
        const invoice = res as Invoice
        if (invoice) {
          setInvoice(invoice)
          if (invoice.notes) {
            setInvoiceNotes(invoice.notes)
          }
        }
      })
    }
  }, [booking?.serviceBookingId])

  useEffect(() => {
    if (projectSetting) {
      setInvoiceNotes(projectSetting.metadata?.invoiceNotes || "")
    }
  }, [projectSetting])

  const handleUpdateStatus = (status: string) => {
    if (booking?.serviceBookingId) {
      dispatch(onUpdateBooking({ serviceBookingId: booking.serviceBookingId, status })).then((response: any) => {
        setBooking(response.payload)
      })
    }
  }

  const handleGenerateInvoice = async () => {
    if (booking?.serviceBookingId) {
      if (invoice) {
        await doUpdateInvoice(invoice.invoiceId, {
          notes: invoiceNotes,
        })
        window.open(`${customerPortalUrl(project?.code!)}/invoice/${invoice.shortUrl}`, '_blank')
        return
      }

      const createdInvoice = await doCreateInvoice({
        sourceId: booking.serviceBookingId,
        sourceType: 'booking',
        subtotal: booking.totalPrice,
        customerId: booking.customerId,
        notes: invoiceNotes,
      }) as Invoice

      if (createdInvoice) {
        setInvoice(createdInvoice)
        window.open(`${customerPortalUrl(project?.code!)}/invoice/${createdInvoice.shortUrl}`, '_blank')
      }
    }
  }

  return (
    <Modal show={!!isOpen} onHide={() => setIsOpen(false)} modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-screen md:w-[70rem] bg-white shadow rounded-md dark:bg-zink-600">
      <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500">
        <Modal.Title className="text-16">{'Booking Details'}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
          <div className="xl:col-span-6">
            <label className="inline-block mb-2 text-base font-medium">Reservation Date:
              <span className="font-bold ml-2">
                {`${moment(booking?.reservationDateTime).format('DD MMM yyyy HH:mm A')} - ${moment(booking?.reservationEndDateTime).format('DD MMM yyyy HH:mm A')}`}
              </span>
            </label>
          </div>
          <div className="xl:col-span-6">
            <label className="inline-block mb-2 text-base font-medium">Reserved At:
              <span className="font-bold ml-2">
                {booking?.createdAt ? moment(booking?.createdAt).format('DD MMM yyyy') : '-'}
              </span>
            </label>
          </div>
          <div className="xl:col-span-6">
            <label className="inline-block mb-2 text-base font-medium">Duration:
              <span className="font-bold ml-2">
                {booking?.reservationDuration} minutes
              </span>
            </label>
          </div>
          <div className="xl:col-span-6">
            <label className="inline-block mb-2 text-base font-medium">Attended By:
              <span className="font-bold ml-2">
                {booking?.attendedByUser?.name}
              </span>
            </label>
          </div>
          <div className="xl:col-span-6">
            <label className="inline-block mb-2 text-base font-medium">Customer Name:
              <span className="font-bold ml-2">
                {booking?.customer?.name}
              </span>
            </label>
          </div>
          <div className="xl:col-span-6">
            <label className="inline-block mb-2 text-base font-medium">Attended Address:
              <span className="font-bold ml-2">
                {booking?.attendedAddress || '-'}
              </span>
            </label>
          </div>
          <div className="xl:col-span-12">
            <label className="inline-block mb-2 text-base font-medium">Total Amount:
              <span className="font-bold ml-2">
                {currencyFormatter(booking?.totalPrice || 0)}
              </span>
            </label>
          </div>
          <div className="xl:col-span-6">
            <label className="inline-block mb-2 text-base font-medium">Payment Method:
              <span className="font-bold ml-2">
                {booking?.paymentMethod || '-'}
              </span>
            </label>
          </div>
          <div className="xl:col-span-6">
            <label className="inline-block mb-2 text-base font-medium">Payment Status:
              <PaymentStatusHelper status={booking?.paymentStatus || 'unpaid'} />
            </label>
          </div>
          <div className="xl:col-span-12">
            <label className="inline-block mb-2 text-base font-medium mr-2">Status:</label>
            <span className={`delivery_status px-2.5 py-0.5 text-xs inline-block font-medium rounded border`}>
              {booking?.status}
            </span>
          </div>
          <div className="xl:col-span-12">
            <label className="inline-block mb-2 text-base font-medium mr-2">Remark:
              <span className="font-bold ml-2">
                {booking?.remark}
              </span>
            </label>
          </div>
          <div className="xl:col-span-12">
            <label className="inline-block mb-2 text-base font-medium mr-2">Invoice Notes:
              <ReactQuill
                id={`invoiceNotes`}
                placeholder="Invoice Notes"
                className="w-full border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                theme="snow"
                value={invoiceNotes}
                onChange={(value) => {
                  setInvoiceNotes(value)
                }}
              />
            </label>
          </div>
          <div className="xl:col-span-12">
            <div className="ltr:lg:text-right rtl:lg:text-left">
              <Link to="#!" data-modal-target="addOrderModal" type="button"
                className="ml-4 text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                onClick={() => handleGenerateInvoice()}
              >
                <span className="align-middle">{'Generate Invoice'}</span>
              </Link>
              {
                booking?.status === serviceBookingStatus.pending &&
                <Fragment>
                  <Link to="#!" data-modal-target="addOrderModal" type="button"
                    className="ml-4 text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    onClick={() => handleUpdateStatus(serviceBookingStatus.confirmed)}
                  >
                    <span className="align-middle">{'Confirm'}</span>
                  </Link>
                  <Link to="#!" data-modal-target="addOrderModal" type="button"
                    className="ml-4 text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    onClick={() => handleUpdateStatus(serviceBookingStatus.cancelled)}
                  >
                    <span className="align-middle">{'Cancel'}</span>
                  </Link>
                </Fragment>
              }
              {
                booking?.status === serviceBookingStatus.confirmed &&
                <Fragment>
                  <Link to="#!" data-modal-target="addOrderModal" type="button"
                    className="ml-4 text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    onClick={() => handleUpdateStatus(serviceBookingStatus.cancelled)}
                  >
                    <span className="align-middle">{'Cancel'}</span>
                  </Link>
                  <Link to="#!" data-modal-target="addOrderModal" type="button"
                    className="ml-4 text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    onClick={() => handleUpdateStatus(serviceBookingStatus.completed)}
                  >
                    <span className="align-middle">{'Complete'}</span>
                  </Link>
                </Fragment>
              }
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default BookingDetailModal